import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, Checkbox } from 'rsuite';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { EncyclopediaRelation, UpsertEncyclopediaRelation } from '@/models/encyclopedia-relation.model';
import { getEncyclopediaRelation, upsertEncyclopediaRelation } from '@/services/encyclopedia-relation.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getEncyclopediaItemsLookup } from '@/services/encyclopedia-item.service';
import { getStoryChaptersLookup } from '@/services/story-chapter.service';


const modelSchema = Schema.Model<UpsertEncyclopediaRelation>({
  id: Schema.Types.NumberType(),
  storyChapterId: Schema.Types.NumberType().isRequired('This field is required.'),
  encyclopediaItemId: Schema.Types.NumberType().isRequired('This field is required.'),
  unlockAtChapterStart: Schema.Types.BooleanType().isRequired('This field is required.'),
});

interface EncyclopediaRelationDrawerProps extends DrawerProps{
  encyclopediaItemId?: number,
  storyChapterId?: number,
  storyId?: number,
  relationId?: number,
};

const EncyclopediaRelationDrawer = (props: EncyclopediaRelationDrawerProps) => {
  const { relationId:id, storyChapterId, storyId, encyclopediaItemId, open, onClose, ...rest } = props;

  const emptyModel: UpsertEncyclopediaRelation = {
    id: undefined,
    storyChapterId: storyChapterId,
    encyclopediaItemId: encyclopediaItemId,
    unlockAtChapterStart: true
  };

  const [formValue, setFormValue] = useState<UpsertEncyclopediaRelation>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
  
  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormValue(emptyModel);
    }
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<EncyclopediaRelation> = 
          await getEncyclopediaRelation(id);
        if (result.success) {
          setFormValue({ ...result.result});
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching encyclopedia relation:${errorModel?.errorMessage}`);        
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<EncyclopediaRelation> = await upsertEncyclopediaRelation(formValue);
      
      if (result.success) {
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue, 
      ...value 
    }));
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" 
      open={open}
      onClose={handleDrawerClose} 
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>{id ? 'Edit a' : 'Add a new'} Encyclopedia Relation</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          {/* <Button onClick={handleDrawerClose} appearance="subtle"> */}
          <Button onClick={() => console.log(formValue)} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-relation' fluid 
          ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}
        >
          <Form.Group>
            <Form.Label>Encyclopedia Item</Form.Label>
            <Form.Control name="encyclopediaItemId" accepter={FetchingSelect} block
              fetchData={(text, page) => getEncyclopediaItemsLookup(text, page)} 
              placeholder="Select an item"
              disabled={!!encyclopediaItemId}
              reFetchOnOpen = {true}
            /> 
          </Form.Group>          
          <Form.Group>
            <Form.Label>Story Chapter</Form.Label>
            <Form.Control name="storyChapterId" accepter={FetchingSelect} block
              fetchData={(text, page) => getStoryChaptersLookup(text, page, storyId)} 
              placeholder="Select an item"
              disabled={!!storyId || !!storyChapterId}
              reFetchOnOpen = {true}
            /> 
          </Form.Group>          
          <Form.Group>
            <Form.Label>Unlock At Story Chapter Start</Form.Label>
            <Form.Control name="unlockAtChapterStart" accepter={Checkbox} 
              checked={!!formValue.unlockAtChapterStart}
              onChange={(val, chk) => setFormValue(pre => ({ ...pre, unlockAtChapterStart: chk }))}
            />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default EncyclopediaRelationDrawer;
